import { useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  Clock3,
  FileQuestion,
  Handshake,
  HelpCircle,
  Hourglass,
  Info,
  ListTodo,
  MessageCircleQuestion,
  Milestone,
  Quote,
  RefreshCw,
  Sparkles,
  Stamp,
  Tag,
  UserRound,
} from 'lucide-react';
import type { Finding, ThreadAnalysis } from '@mailpilot/shared';
import { api } from '../../lib/api';
import { qk } from '../../lib/keys';
import { relativeTime, shortDate } from '../../lib/format';
import type { ThreadDetail } from '../../hooks/useThreads';
import { Badge } from '../ui/Badge';
import { Button, IconButton } from '../ui/Button';
import { AiStateView, type AiPhase } from './AiState';

const KIND: Record<Finding['kind'], { label: string; icon: ReactNode }> = {
  unanswered_question: { label: 'Unanswered questions', icon: <MessageCircleQuestion className="h-4 w-4" aria-hidden /> },
  missing_document: { label: 'Missing documents', icon: <FileQuestion className="h-4 w-4" aria-hidden /> },
  pending_approval: { label: 'Pending approvals', icon: <Stamp className="h-4 w-4" aria-hidden /> },
  requested_information: { label: 'Requested information', icon: <ClipboardList className="h-4 w-4" aria-hidden /> },
  promise: { label: 'Promises', icon: <Handshake className="h-4 w-4" aria-hidden /> },
  deadline: { label: 'Deadlines', icon: <CalendarClock className="h-4 w-4" aria-hidden /> },
  stalled: { label: 'Stalled', icon: <Hourglass className="h-4 w-4" aria-hidden /> },
  follow_up: { label: 'Follow-up opportunities', icon: <Milestone className="h-4 w-4" aria-hidden /> },
};

const OWNER: Record<ThreadAnalysis['nextReplyOwner'], { label: string; tone: 'warn' | 'info' | 'neutral' | 'success'; icon: ReactNode }> = {
  me: { label: 'You owe the next reply', tone: 'warn', icon: <UserRound className="h-3 w-3" aria-hidden /> },
  them: { label: 'Waiting on them', tone: 'info', icon: <Clock3 className="h-3 w-3" aria-hidden /> },
  nobody: { label: 'No reply needed', tone: 'success', icon: <CheckCircle2 className="h-3 w-3" aria-hidden /> },
  unknown: { label: 'Unclear who replies next', tone: 'neutral', icon: <HelpCircle className="h-3 w-3" aria-hidden /> },
};

function MicroLabel({ icon, children }: { icon?: ReactNode; children: ReactNode }) {
  return (
    <h3 className="mb-2 flex items-center gap-1.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-subtle">
      {icon}
      {children}
    </h3>
  );
}

export function AnalysisCard({ threadId, analysis, onEvidence }: { threadId: string; analysis: ThreadAnalysis | null; onEvidence: (messageId: string) => void }) {
  const qc = useQueryClient();
  const [expanded, setExpanded] = useState(true);
  const m = useMutation({
    mutationFn: (force: boolean) => api('POST /api/threads/:id/analyse', { params: { id: threadId }, body: { force } }),
    onSuccess: (r) => qc.setQueryData<ThreadDetail>(qk.thread(threadId), (prev) => (prev ? { ...prev, analysis: r.analysis } : prev)),
  });
  const a = m.data?.analysis ?? analysis;
  const phase: AiPhase = m.isPending ? 'thinking' : m.isError ? 'error' : a ? 'result' : 'idle';
  const groups = new Map<Finding['kind'], Finding[]>();
  a?.findings.forEach((f) => groups.set(f.kind, [...(groups.get(f.kind) ?? []), f]));
  const bodyId = `analysis-${threadId}`;

  return (
    <section aria-label="Conversation summary" className="overflow-hidden rounded-card border border-hairline bg-surface shadow-card">
      {/* accent-tinted header strip */}
      <div
        className="flex items-center gap-3 border-b border-line/60 px-5 py-3"
        style={{ background: 'linear-gradient(90deg, rgb(var(--primary) / 0.1), rgb(var(--primary) / 0.03) 60%, transparent)' }}
      >
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[10px] bg-primary text-primary-fg shadow-primary">
          <Sparkles className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="text-[0.9375rem] font-semibold leading-snug text-ink">Conversation summary</h2>
          {a && (
            <p className="truncate text-[0.6875rem] text-muted">
              {a.generatedBy} · {relativeTime(a.generatedAt)}
            </p>
          )}
        </div>
        {a && (
          <div className="flex shrink-0 items-center gap-0.5">
            <IconButton size="sm" label="Re-analyse conversation" icon={<RefreshCw className={clsx('h-4 w-4', m.isPending && 'animate-spin')} />} onClick={() => m.mutate(true)} disabled={m.isPending} />
            <IconButton
              size="sm"
              label={expanded ? 'Collapse summary' : 'Expand summary'}
              aria-expanded={expanded}
              aria-controls={bodyId}
              icon={<ChevronDown className={clsx('h-4 w-4 transition-transform', expanded && 'rotate-180')} />}
              onClick={() => setExpanded((e) => !e)}
            />
          </div>
        )}
      </div>

      <div id={bodyId} className="px-5 py-4">
        <AiStateView
          phase={phase}
          error={m.error}
          onRetry={() => m.mutate(!!a)}
          thinkingLabel="Reading the conversation…"
          idle={
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="max-w-prose text-[0.8125rem] leading-relaxed text-muted">
                Get what this conversation is about, who owes the next reply, and open questions — each linked to the message that supports it.
              </p>
              <Button variant="primary" size="sm" icon={<Sparkles className="h-4 w-4" />} onClick={() => m.mutate(false)}>
                Analyse
              </Button>
            </div>
          }
          result={
            a && (
              <div className="space-y-4">
                {/* About + position */}
                <div className="space-y-1.5">
                  <p className="reading max-w-[68ch] text-ink">{a.about}</p>
                  {expanded && (
                    <p className="max-w-[68ch] text-[0.8125rem] leading-relaxed text-muted">
                      <span className="font-medium text-ink">Latest position · </span>
                      {a.latestPosition}
                    </p>
                  )}
                </div>

                {/* Status pills */}
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={OWNER[a.nextReplyOwner].tone} icon={OWNER[a.nextReplyOwner].icon} className="!px-2.5 !py-1">
                    {OWNER[a.nextReplyOwner].label}
                  </Badge>
                  {a.category && (
                    <Badge icon={<Tag className="h-3 w-3" aria-hidden />} className="!px-2.5 !py-1 capitalize">
                      {a.category}
                    </Badge>
                  )}
                  {expanded && a.nextReplyReason && <span className="text-xs text-muted">{a.nextReplyReason}</span>}
                </div>

                {expanded && a.insufficientEvidence && (
                  <div className="flex items-start gap-2 rounded-xl border border-warn/30 bg-warn-soft px-3 py-2 text-[0.8125rem] text-warn-ink">
                    <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    <span>Insufficient evidence — the imported messages don't contain enough to be sure. Treat these points as tentative.</span>
                  </div>
                )}

                {/* Findings */}
                {expanded && groups.size > 0 && (
                  <div className="space-y-4 border-t border-line/60 pt-4">
                    {Array.from(groups.entries()).map(([kind, list]) => (
                      <div key={kind}>
                        <MicroLabel icon={<span className="text-subtle [&>svg]:h-3.5 [&>svg]:w-3.5">{KIND[kind].icon}</span>}>
                          {KIND[kind].label}
                          <span className="font-medium normal-case tracking-normal text-subtle">· {list.length}</span>
                        </MicroLabel>
                        <ul className="space-y-1">
                          {list.map((f, i) => (
                            <li key={i} className="flex items-start gap-3 rounded-xl px-2.5 py-2 transition-colors hover:bg-raised/70">
                              <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-raised text-muted">{KIND[kind].icon}</span>
                              <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                                  <p className="min-w-0 flex-1 text-[0.8125rem] leading-relaxed text-ink">{f.text}</p>
                                  <span className="flex shrink-0 flex-wrap items-center gap-1.5">
                                    {f.dueDate && (
                                      <Badge tone="warn" icon={<CalendarClock className="h-3 w-3" aria-hidden />}>
                                        Due {shortDate(f.dueDate)}
                                      </Badge>
                                    )}
                                    {f.owner !== 'unknown' && <Badge tone={f.owner === 'me' ? 'primary' : 'neutral'}>{f.owner === 'me' ? 'On you' : 'On them'}</Badge>}
                                    <span
                                      className={clsx('inline-flex items-center gap-1 text-[0.6875rem] font-medium', f.basis === 'observed' ? 'text-success-ink' : 'text-info-ink')}
                                      title={f.basis === 'observed' ? 'Stated directly in a message' : 'Suggested by MailPilot — not stated directly'}
                                    >
                                      <span className={clsx('h-1.5 w-1.5 rounded-full', f.basis === 'observed' ? 'bg-success' : 'bg-info')} aria-hidden />
                                      {f.basis === 'observed' ? 'Observed' : 'Inferred'}
                                    </span>
                                  </span>
                                </div>
                                {f.evidence.length > 0 && (
                                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                                    {f.evidence.map((ev, j) => (
                                      <button
                                        key={j}
                                        type="button"
                                        onClick={() => onEvidence(ev.messageId)}
                                        className="inline-flex max-w-full items-center gap-1 rounded-md bg-primary-soft/70 px-2 py-0.5 text-[0.6875rem] font-medium text-primary-ink transition-colors hover:bg-primary-soft"
                                        title="Show the supporting message"
                                      >
                                        <Quote className="h-3 w-3 shrink-0" aria-hidden />
                                        <span className="max-w-[26rem] truncate italic">“{ev.quote}”</span>
                                      </button>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                )}

                {/* Next actions */}
                {expanded && a.nextActions.length > 0 && (
                  <div className="border-t border-line/60 pt-4">
                    <MicroLabel icon={<ListTodo className="h-3.5 w-3.5" aria-hidden />}>Next actions</MicroLabel>
                    <ul className="space-y-1" aria-label="Suggested next actions">
                      {a.nextActions.map((n, i) => (
                        <li key={i} className="flex items-start gap-3 rounded-xl px-2.5 py-1.5 text-[0.8125rem] leading-relaxed text-ink">
                          <span className="mt-[5px] h-[15px] w-[15px] shrink-0 rounded-[5px] border-[1.5px] border-line bg-surface" aria-hidden />
                          <span>{n}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {expanded && (
                  <p className="text-[0.6875rem] leading-relaxed text-subtle">
                    Based on {a.analysedMessageCount} of {a.totalMessageCount} message{a.totalMessageCount === 1 ? '' : 's'} in this thread
                    {a.analysedMessageCount < a.totalMessageCount && ' (some messages were not analysed)'} · Generated by {a.generatedBy} · {relativeTime(a.generatedAt)}
                  </p>
                )}
              </div>
            )
          }
        />
      </div>
    </section>
  );
}
